import React, { useState, useEffect, useCallback } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
import Dropzone from 'react-dropzone';

import { LinkTypes } from 'helpers/enumerations';
import { SnackbarDismiss } from 'GeminiViewerComponent/components/SnackbarDismiss';
import { FormActions } from 'helpers/form-action';
import { snackbarHandler } from 'GeminiViewerComponent/helpers/snackbar-handler';
import { panelStyles } from 'GeminiViewerComponent/components/styles';
import { makeFormStyles } from '../styles';

import {
    setProgress,
    activateLoading,
    deactivateLoading,
} from 'GeminiViewerComponent/features/globals/loadingProgressSlice';
import { closePanel } from 'features/common/formSlice';
import { selectDefaultTheme } from 'GeminiViewerComponent/features/globals/themeSlice';
import SetDirtyForm from 'forms/SetDirtyForm';
import { LoadingLogo } from 'GeminiViewerComponent/components/LoadingLogo/LoadingLogo';
import { CompanionAppMode } from 'GeminiViewerComponent/helpers';
import {
    createCompanionApp,
    editCompanionApp,
    fetchCompanionAppById,
    fetchCompanionAppUsageById,
    getCompanionAppUsage,
} from 'GeminiViewerComponent/features/companionApps/companionAppsSlice';
import { UsageDisplay } from 'components';
import { FormSelectField, FormTextField } from 'components/TextField';

const CompanionForm = ({ formAction, companionAppId }) => {
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const [loaded, setLoaded] = useState(formAction.id === FormActions.Create.id ? true : false);
    const theme = useSelector(selectDefaultTheme);
    const classes = makeFormStyles(theme);
    const panelClasses = panelStyles(theme);
    const INIT_VALUES = {
        display_name: '',
        app_icon_data: '',
        app_icon_url: '',
        app_url: '',
        app_mode: '',
    };
    const [initValues, setInitValues] = useState({
        ...INIT_VALUES,
    });
    const companionAppUsage = useSelector(getCompanionAppUsage);

    useEffect(() => {
        if (formAction.id === FormActions.Edit.id) {
            const fetchData = async () => {
                await dispatch(fetchCompanionAppUsageById(companionAppId));
                let objectData = await dispatch(fetchCompanionAppById(companionAppId));
                let fetchedObject = objectData.payload;

                setInitValues({
                    ...fetchedObject,
                });
                setLoaded(true);
            };
            fetchData();
        } else {
            setInitValues({
                ...INIT_VALUES,
            });
        }
    }, [companionAppId]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleSubmit = async (values, setSubmitting, resetForm, setErrors) => {
        dispatch(activateLoading({ showProgress: true }));
        let resultAction = null;
        try {
            switch (formAction.id) {
                case FormActions.Edit.id:
                    dispatch(activateLoading({ showProgress: true }));
                    dispatch(
                        setProgress({
                            progress: 0,
                            label: 'Step 1 of 2: Uploading',
                            noAnimation: true,
                        })
                    );

                    resultAction = await dispatch(editCompanionApp({ ...values }));
                    if (!resultAction.payload?.error) {
                        dispatch(closePanel({ formKey: 'companionForm' }));
                    }
                    dispatch(deactivateLoading());
                    break;
                case FormActions.Create.id:
                    dispatch(
                        setProgress({
                            progress: 0,
                            label: 'Step 1 of 2: Uploading',
                            noAnimation: true,
                        })
                    );
                    resultAction = await dispatch(createCompanionApp({ ...values, dispatch }));
                    if (!resultAction.error) {
                        resetForm();
                    }
                    break;
                default:
                    break;
            }

            if (!resultAction.payload?.error) {
                dispatch(closePanel({ formKey: 'companionForm' }));
                resetForm();
                const { message, variant } = snackbarHandler(resultAction.meta.requestStatus, formAction.label);
                enqueueSnackbar(message, {
                    action: (key) => <SnackbarDismiss key={key} />,
                    variant: variant,
                });
            } else {
                if (resultAction?.payload?.error?.errors) {
                    setErrors({ ...resultAction?.payload?.error?.errors });
                } else {
                    throw new Error(resultAction?.payload?.error);
                }
            }
        } catch (err) {
            enqueueSnackbar(err?.message !== undefined ? err.message : 'Create Failed', {
                action: (key) => <SnackbarDismiss key={key} />,
                variant: 'error',
            });
        }
        dispatch(deactivateLoading());
        setSubmitting(false);
    };

    const onDrop = useCallback(({ file, setFieldValue }) => {
        if (file) {
            let reader = new FileReader();
            reader.onload = (e) => {
                setFieldValue('app_icon_image', file);
                setFieldValue('app_icon_url', e.target.result);
            };
            reader.readAsDataURL(file);
        } else {
            setFieldValue('app_icon_image', '');
        }
    }, []);

    const companionAppValidation = Yup.object({
        display_name: Yup.string().max(100, 'Must be 100 characters or less').required('Required'),
        app_mode: Yup.string().required('Required.'),
    });

    return !loaded ? (
        <LoadingLogo styleClassName={classes.loadingSvg} />
    ) : (
        <Formik
            enableReinitialize={true}
            initialValues={{ ...initValues }}
            validationSchema={companionAppValidation}
            onSubmit={(values, { setSubmitting, resetForm, setErrors }) => {
                handleSubmit(values, setSubmitting, resetForm, setErrors);
            }}
        >
            {({ values, isSubmitting, errors, touched }) => (
                <Form className={classes.form}>
                    <div className={classes.formHeader}>Companion App Details</div>
                    <FormTextField
                        autoFocus
                        label="Companion App Name"
                        name="display_name"
                        placeholder="Companion App Name"
                    />
                    <Field className={classes.input} name="app_icon_image" type="file">
                        {({ field: { value }, form: { setFieldValue } }) => (
                            <div className={panelClasses.dropzoneContainer}>
                                <Dropzone
                                    accept={LinkTypes.Image.contentTypes.map((at) => at.extension)}
                                    maxFiles={1}
                                    maxSize={LinkTypes.Image.fileSizeLimit}
                                    multiple={false}
                                    onDropAccepted={(files) => {
                                        onDrop({
                                            file: files[0],
                                            setFieldValue,
                                        });
                                    }}
                                >
                                    {({ getRootProps, getInputProps }) => (
                                        <div
                                            {...getRootProps()}
                                            className={panelClasses.dropzoneArea}
                                            style={{
                                                backgroundImage: `url(${values.app_icon_url})`,
                                                backgroundRepeat: 'no-repeat',
                                                backgroundSize: 'contain',
                                                backgroundPosition: 'center center',
                                                borderColor: `${errors?.image && touched?.image ? 'red' : 'black'}`,
                                            }}
                                        >
                                            <input {...getInputProps()} />
                                            {!values.app_icon_url && (
                                                <div className={panelClasses.dropzoneText}>
                                                    Upload Companion App Icon
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </Dropzone>
                                <ErrorMessage component="div" className={classes.inputErr} name="app_icon_url" />
                            </div>
                        )}
                    </Field>
                    <FormTextField label="Companion App URL" name="app_url" placeholder="Companion App URL" />
                    <FormSelectField
                        name="app_mode"
                        label="Companion App Default Mode"
                        entities={CompanionAppMode}
                        valueProperty="id"
                        nameProperty="display_name"
                        keyProperty="id"
                    />
                    <SetDirtyForm />
                    <button className={classes.submit} type="submit" disabled={isSubmitting}>
                        {formAction.buttonLabel}
                    </button>
                    {formAction.id === FormActions.Edit.id && Object.keys(companionAppUsage ?? {})?.length > 0 && (
                        <UsageDisplay
                            data={companionAppUsage}
                            listHeader="Companion App Usage"
                            id="companion-usage-id"
                        />
                    )}
                </Form>
            )}
        </Formik>
    );
};

export { CompanionForm };
